import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Tooltip,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Button,
  CircularProgress
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import MetricCard from "../MetricCard";
import ListSection from "../ListSection";
import EvolutionChartSection from "../EvolutionChartSection";
import SoldProductItem from "../SoldProductItem";
import SoldCategoryItem from "../SoldCategoryItem";
import { LineChart } from "../Charts";
import { formatBRL, formatNumber, formatDays } from "../utils/formatters";

export default function VisaoGeralTab({
  metricas,
  averagePrice,
  cancellationRate,
  catalogAge,
  productLifecycle,
  onUpdateSku,
  loadingSkuData,
  loadingMetrics
}) {
  const [skuSearch, setSkuSearch] = useState("");
  const [skuFilter, setSkuFilter] = useState("");
    const handleSkuSearch = (value) => {
    // Verifica se value é um evento ou um valor de string
    const isEvent = value && typeof value === 'object' && value.hasOwnProperty('_reactName');
    
    // Se for um evento ou undefined, usa o estado interno do componente
    const searchValue = isEvent || value === undefined ? skuSearch : value;
    setSkuFilter(searchValue);

    // Se temos uma função de atualização e um valor de SKU válido, chama a API
    if (onUpdateSku && searchValue && typeof searchValue === 'string' && searchValue.trim() !== "") {
      // Define o valor do input de pesquisa para manter sincronizado
      setSkuSearch(searchValue.trim());
      onUpdateSku(searchValue.trim());
    }
  };
  
  const handleSkuClear = () => {
    setSkuSearch("");
    setSkuFilter("");
    
    // Limpa o filtro na API também, voltando para o SKU padrão
    if (onUpdateSku) {
      onUpdateSku(undefined);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Cards de resumo do catálogo */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%', background: 'linear-gradient(to bottom, #fff, #f9faff)' }}>
            <MetricCard
              title="Total de Produtos"
              value={metricas.totalProdutos}
              subtitle={
                <Tooltip title="Número total de SKUs cadastrados no canal selecionado" placement="bottom" arrow>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'help' }}>
                    <InfoOutlinedIcon fontSize="small" color="info" />
                    <span>Total de SKUs cadastrados</span>
                  </Box>
                </Tooltip>
              }
            />
          </Paper>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%', background: 'linear-gradient(to bottom, #fff, #f1f8e9)' }}>
            <MetricCard
              title="Produtos Ativos"
              value={metricas.produtosAtivos}              subtitle={
                <Tooltip title="SKUs disponíveis para venda no marketplace" placement="bottom" arrow>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'help' }}>
                    <InfoOutlinedIcon fontSize="small" color="success" />
                    <span>{metricas.percentAtivos ? metricas.percentAtivos.toFixed(1) : ((metricas.produtosAtivos / metricas.totalProdutos) * 100 || 0).toFixed(1)}% do catálogo</span>
                  </Box>
                </Tooltip>
              }
            />
          </Paper>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%', background: 'linear-gradient(to bottom, #fff, #e8f5e9)' }}>
            <MetricCard
              title="Produtos Em Estoque"
              value={metricas.produtosEmEstoque}              subtitle={
                <Tooltip title="SKUs com estoque disponível para venda" placement="bottom" arrow>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'help' }}>
                    <InfoOutlinedIcon fontSize="small" color="success" />
                    <span>{metricas.percentEmEstoque ? metricas.percentEmEstoque.toFixed(1) : ((metricas.produtosEmEstoque / metricas.totalProdutos) * 100 || 0).toFixed(1)}% do catálogo</span>
                  </Box>
                </Tooltip>
              }
            />
          </Paper>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%', background: 'linear-gradient(to bottom, #fff, #fff8e1)' }}>
            <MetricCard
              title="Produtos Inativos"
              value={metricas.produtosInativos}              subtitle={
                <Tooltip title="SKUs não disponíveis para venda no marketplace" placement="bottom" arrow>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'help' }}>
                    <InfoOutlinedIcon fontSize="small" color="warning" />
                    <span>{metricas.percentInativos ? metricas.percentInativos.toFixed(1) : ((metricas.produtosInativos / metricas.totalProdutos) * 100 || 0).toFixed(1)}% do catálogo</span>
                  </Box>
                </Tooltip>
              }
            />
          </Paper>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%', background: 'linear-gradient(to bottom, #fff, #ffebee)' }}>
            <MetricCard
              title="Produtos em Falta"
              value={metricas.produtosEmFalta}              subtitle={
                <Tooltip title="SKUs sem estoque disponível" placement="bottom" arrow>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'help' }}>
                    <InfoOutlinedIcon fontSize="small" color="error" />
                    <span>{metricas.percentEmFalta ? metricas.percentEmFalta.toFixed(1) : ((metricas.produtosEmFalta / metricas.totalProdutos) * 100 || 0).toFixed(1)}% do catálogo</span>
                  </Box>
                </Tooltip>
              }
            />
          </Paper>
        </Box>
      </Box>
      {/* Listas mais vendidos e categorias */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        <Box sx={{ flex: 1 }}>
          <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <ListSection
              title="Produtos Mais Vendidos"
              items={metricas.produtosMaisVendidos}
              emptyMessage="Nenhum dado disponível para o período selecionado"
              renderItem={(item, idx) => (
                <SoldProductItem key={idx} product={item} />
              )}
            />
          </Paper>
        </Box>
        <Box sx={{ flex: 1 }}>          <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%' }}>            <ListSection
              title="Categorias Mais Vendidas"
              items={metricas.categoriasMaisVendidas}
              emptyMessage="Nenhum dado disponível para o período selecionado"
              isLoading={loadingMetrics}
              renderItem={(item, idx) => {
                // Encontrar o valor máximo de receita para determinar porcentagens
                const maxRevenue = metricas.categoriasMaisVendidas.length > 0
                  ? Math.max(...metricas.categoriasMaisVendidas.map(cat => cat.receita))
                  : 0;
                
                return (
                  <SoldCategoryItem 
                    key={idx} 
                    category={item} 
                    maxRevenue={maxRevenue}
                  />
                );
              }}
            />
          </Paper>
        </Box>
      </Box>
      
      {/* Visualização de ciclo de vida do produto */}
      <Box container spacing={2}>
        <Box item xs={12}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1a237e' }}>
                Ciclo de Vida dos Produtos
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: '#607d8b' }}>
                Evolução mensal de vendas, receita e pedidos de um SKU ao longo do tempo. Utilize as opções de filtro abaixo.
              </Typography>
              
              {productLifecycle && productLifecycle.monthlySales && productLifecycle.monthlySales.length > 0 ? (
                <>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' }, 
                    alignItems: { xs: 'flex-start', sm: 'center' }, 
                    gap: 2, 
                    mb: 2,
                    flexWrap: 'wrap'
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' }, 
                      alignItems: { xs: 'flex-start', sm: 'center' }, 
                      gap: 2,
                      minWidth: { xs: '100%', sm: 'auto' }
                    }}>
                      <TextField
                        label="Filtrar SKU"
                        size="small"
                        value={skuSearch}
                        onChange={e => setSkuSearch(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSkuSearch(); }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {loadingSkuData ? (
                                <CircularProgress size={20} color="primary" />
                              ) : (                              <IconButton onClick={() => handleSkuSearch()} edge="end" size="small">
                                  <SearchIcon />
                              </IconButton>
                              )}
                            </InputAdornment>
                          )
                        }}
                        sx={{ 
                          width: { xs: '100%', sm: 220 }, 
                          background: '#f7f7f7', 
                          borderRadius: 1 
                        }}
                      />
                      
                      <Box sx={{ 
                        display: 'flex',
                        gap: 1 
                      }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<RefreshIcon />}
                          onClick={handleSkuClear}
                          disabled={loadingSkuData}
                          sx={{ 
                            height: 40, 
                            borderColor: '#e0e0e0',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Limpar
                        </Button>
                      </Box>
                    </Box>
                    
                    {productLifecycle.product && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip 
                          label={`SKU: ${productLifecycle.product.skuTitle}`} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          onDelete={handleSkuClear}
                        />
                        {productLifecycle.product.productTitle && (
                          <Typography variant="body2" color="text.secondary">
                            {productLifecycle.product.productTitle}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                  
                  {loadingSkuData ? (
                    <Box sx={{ 
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%', 
                      height: 350,
                      flexDirection: 'column',
                      gap: 2
                    }}>
                      <CircularProgress size={60} />
                      <Typography variant="body1" color="text.secondary">
                        Carregando dados do SKU {skuFilter}...
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 450 }}>
                        Analisando histórico de vendas e calculando métricas de desempenho. Isso pode levar alguns instantes.
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%', 
                      height: 350 
                    }}>
                      <LineChart
                        xAxis={[{ 
                          data: productLifecycle.monthlySales.map((m) => m.yearMonth),
                          label: 'Período',
                          scaleType: 'band'
                        }]}
                        series={[
                          { 
                            data: productLifecycle.monthlySales.map((m) => m.salesCount || 0), 
                            label: "Vendas",
                            color: '#1a237e'
                          },
                          { 
                            data: productLifecycle.monthlySales.map((m) => m.revenue || 0), 
                            label: "Receita (R$ x100)",
                            color: '#43a047'
                          }
                        ]}
                        width={undefined}
                        height={300}
                      />
                    </Box>
                  )}
                  
                  {/* Legenda do gráfico */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, backgroundColor: '#1a237e', borderRadius: '50%' }}></Box>
                      <Typography variant="body2">Vendas</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, backgroundColor: '#43a047', borderRadius: '50%' }}></Box>
                      <Typography variant="body2">Receita (R$ x100)</Typography>
                    </Box>
                  </Box>
                  
                  {/* Dados Adicionais */}
                  {productLifecycle.product && (
                    <Box sx={{ 
                      mt: 3,
                      pt: 2,
                      borderTop: '1px solid #eee', 
                      display: 'flex', 
                      flexWrap: 'wrap',
                      gap: 3,
                      justifyContent: 'space-around'
                    }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Total de Vendas</Typography>
                        <Typography variant="h6" fontWeight={600} color="primary">
                          {formatNumber(productLifecycle.monthlySales.reduce((acc, item) => acc + (item.salesCount || 0), 0), 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Receita Total</Typography>
                        <Typography variant="h6" fontWeight={600} color="#43a047">
                          {formatBRL(productLifecycle.monthlySales.reduce((acc, item) => acc + (item.revenue || 0), 0))}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Média Mensal</Typography>
                        <Typography variant="h6" fontWeight={600} color="primary">
                          {formatNumber(productLifecycle.monthlySales.reduce((acc, item) => acc + (item.salesCount || 0), 0) / (productLifecycle.monthlySales.length || 1), 1)} vendas/mês
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <EvolutionChartSection 
                  onFiltrar={handleSkuSearch} 
                  onLimpar={handleSkuClear} 
                  disableActions={loadingSkuData} 
                />
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
